import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Returns a human-readable elapsed-time string between the given date and now.
 *
 * Examples:
 *   - same day        -> "Today"
 *   - 1 day           -> "1 day"
 *   - 2..29 days      -> "N days"
 *   - 30..364 days    -> "1 month" / "N months"
 *   - 365+ days       -> "1 year"  / "N years"
 *
 * Returns "-" for null/undefined/invalid dates and for dates in the future.
 *
 * Marked `pure: true` (the default) so Angular only re-evaluates it when the
 * input reference changes. That makes it safe to use inside *ngFor without
 * the per-change-detection cost of a method call.
 */
@Pipe({
  name: 'daysAgo',
  standalone: true,
  pure: true,
})
export class DaysAgoPipe implements PipeTransform {
  private translocoService = inject(TranslocoService);

  transform(value: string | Date | null | undefined): string {
    if (!value) {
      return '-';
    }

    const createdDate = new Date(value);
    if (isNaN(createdDate.getTime())) {
      return '-';
    }

    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return '-';
    }
    if (diffDays === 0) {
      return this.translocoService.translate('today');
    }
    if (diffDays === 1) {
      return `1 ${this.translocoService.translate('day')}`;
    }
    if (diffDays < 30) {
      return `${diffDays} ${this.translocoService.translate('days')}`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1
        ? `1 ${this.translocoService.translate('month')}`
        : `${months} ${this.translocoService.translate('months')}`;
    }
    const years = Math.floor(diffDays / 365);
    return years === 1
      ? `1 ${this.translocoService.translate('year')}`
      : `${years} ${this.translocoService.translate('years')}`;
  }
}
